import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X, Send, Mail, Clock, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";

const BASE_URL = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";

async function fetchWithAuth(path: string, options?: RequestInit) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.headers ?? {}),
    },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`);
  return data;
}

interface Message {
  id: number;
  subject: string;
  body: string;
  sentAt: string;
}

interface Props {
  userId: number;
  userName: string;
  userEmail: string;
  onClose: () => void;
}

const MESSAGE_TEMPLATES = [
  {
    label: "Welcome",
    subject: "Welcome to Flight Deck!",
    body: `Hi {name},\n\nWelcome to Flight Deck — Canada's aviation exam prep platform!\n\nWe're thrilled to have you on board. Here's how to get started:\n\n  • Head to "Request Takeoff" to begin a practice session\n  • Check "Turbulence Zones" to identify your weak areas\n  • Review "Sector Map" for a breakdown by category\n\nIf you have any questions, don't hesitate to reach out through the Tower page.\n\nClear skies,\nThe Flight Deck Team`,
  },
  {
    label: "Account Approved",
    subject: "Your account has been approved ✓",
    body: `Hi {name},\n\nGreat news — your Flight Deck account has been approved!\n\nYou can now log in and begin your CPL exam prep. Your dashboard is ready and waiting.\n\nTip: Start with a full practice session to get your baseline score, then use Turbulence Zones to focus your study time.\n\nGood luck with your studies!\n\nClear skies,\nThe Flight Deck Team`,
  },
  {
    label: "Account Rejected",
    subject: "Flight Deck — Account Application",
    body: `Hi {name},\n\nThank you for applying to Flight Deck.\n\nAfter reviewing your application, we're unable to approve your account at this time.\n\nIf you believe this is an error or would like more information, please contact us through the Tower page.\n\nClear skies,\nThe Flight Deck Team`,
  },
  {
    label: "Check-in",
    subject: "How is your exam prep going?",
    body: `Hi {name},\n\nJust checking in to see how your studies are progressing!\n\nRemember — consistent daily practice is the best way to build confidence before your Transport Canada exam. Even 15–20 questions a day adds up fast.\n\nLet us know if there's anything we can help with.\n\nClear skies,\nThe Flight Deck Team`,
  },
  {
    label: "Inactivity",
    subject: "We miss you at Flight Deck!",
    body: `Hi {name},\n\nWe noticed you haven't been on Flight Deck in a while — no worries, life gets busy!\n\nWhenever you're ready to get back in the cockpit, your progress is right where you left it. Jump into a quick 10-question session to warm up.\n\nClear skies,\nThe Flight Deck Team`,
  },
  {
    label: "Exam Encouragement",
    subject: "You've got this — exam day tips",
    body: `Hi {name},\n\nGood luck on your upcoming Transport Canada exam!\n\nA few tips from the team:\n\n  • Get a full night's sleep before exam day\n  • Read each question carefully — don't rush\n  • Trust your preparation; you've put in the work\n  • Flag uncertain questions and revisit them\n\nYou're ready. Clear skies ahead!\n\nThe Flight Deck Team`,
  },
  {
    label: "Subscription Thank You",
    subject: "Thanks for upgrading your Flight Deck plan",
    body: `Hi {name},\n\nThank you for upgrading your Flight Deck subscription!\n\nYour support helps us keep improving the platform with new questions, features, and study tools.\n\nMake the most of your plan — check out the Flight Logbook for detailed analytics on your performance trends.\n\nClear skies,\nThe Flight Deck Team`,
  },
  {
    label: "Study Tip",
    subject: "Flight Deck study tip for you",
    body: `Hi {name},\n\nHere's a quick tip to improve your exam prep:\n\n📌 Focus on your weakest categories first.\n\nHead to "Turbulence Zones" in your dashboard — it shows exactly which topics are dragging your score down. Spending 10 minutes a day on your bottom two categories can dramatically improve your overall accuracy within a week.\n\nKeep climbing!\n\nThe Flight Deck Team`,
  },
  {
    label: "Maintenance Notice",
    subject: "Scheduled maintenance — Flight Deck",
    body: `Hi {name},\n\nWe wanted to give you a heads-up that Flight Deck will be undergoing scheduled maintenance.\n\nDuring this time the platform may be briefly unavailable. We apologize for any inconvenience and will have everything back up as quickly as possible.\n\nThank you for your patience.\n\nClear skies,\nThe Flight Deck Team`,
  },
];

export function UserMessageModal({ userId, userName, userEmail, onClose }: Props) {
  const qc = useQueryClient();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [sent, setSent] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  const { data: history = [] } = useQuery<Message[]>({
    queryKey: ["admin-user-messages", userId],
    queryFn: () => fetchWithAuth(`/api/admin/users/${userId}/messages`),
  });

  const sendMessage = useMutation({
    mutationFn: () => fetchWithAuth(`/api/admin/users/${userId}/message`, {
      method: "POST",
      body: JSON.stringify({ subject: subject.trim(), body: body.trim() }),
    }),
    onSuccess: () => {
      setSent(true);
      qc.invalidateQueries({ queryKey: ["admin-user-messages", userId] });
    },
  });

  function applyTemplate(template: typeof MESSAGE_TEMPLATES[0]) {
    setSubject(template.subject);
    setBody(template.body.replace("{name}", userName.split(" ")[0]));
    setShowTemplates(false);
  }

  const inputCls = "w-full bg-background border border-input rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring text-foreground placeholder:text-muted-foreground/40";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-card border border-border rounded-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-secondary/30">
          <div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-primary" />
              <span className="font-bold font-mono text-sm tracking-wider text-foreground">SEND MESSAGE</span>
            </div>
            <div className="text-xs text-muted-foreground font-mono mt-0.5">To: {userName} · {userEmail}</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground/50 hover:text-foreground transition-colors p-1">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {sent ? (
            <div className="text-center py-6 space-y-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center mx-auto">
                <Send className="w-5 h-5 text-primary" />
              </div>
              <p className="font-mono font-bold text-foreground tracking-wider">MESSAGE SENT</p>
              <p className="text-xs text-muted-foreground font-mono">
                {userEmail} will receive your message by email (if SMTP is configured) and it's saved in the message log.
              </p>
              <button
                onClick={() => { setSubject(""); setBody(""); setSent(false); }}
                className="text-xs font-mono text-primary hover:underline"
              >
                Send another
              </button>
            </div>
          ) : (
            <>
              {/* Template picker */}
              <div>
                <button
                  onClick={() => setShowTemplates(v => !v)}
                  className="text-xs font-mono text-primary/70 hover:text-primary transition-colors flex items-center gap-1"
                >
                  {showTemplates ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  Use a template
                </button>
                {showTemplates && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {MESSAGE_TEMPLATES.map(t => (
                      <button
                        key={t.label}
                        onClick={() => applyTemplate(t)}
                        className="text-xs font-mono border border-border bg-secondary/50 hover:bg-secondary px-2.5 py-1 rounded transition-colors text-muted-foreground hover:text-foreground"
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Subject */}
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  placeholder="Message subject"
                  className={inputCls}
                  autoFocus
                />
              </div>

              {/* Body */}
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Message</label>
                <textarea
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  placeholder="Type your message here..."
                  rows={6}
                  className={cn(inputCls, "resize-none")}
                />
              </div>

              {sendMessage.isError && (
                <div className="text-xs text-destructive font-mono border border-destructive/30 bg-destructive/10 px-3 py-2 rounded">
                  {(sendMessage.error as Error)?.message ?? "Failed to send"}
                </div>
              )}

              <button
                onClick={() => sendMessage.mutate()}
                disabled={sendMessage.isPending || !subject.trim() || !body.trim()}
                className="w-full bg-primary/10 border border-primary/40 text-primary font-mono text-xs tracking-widest py-2.5 hover:bg-primary/20 hover:border-primary disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 rounded-md"
              >
                {sendMessage.isPending
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Sending...</>
                  : <><Send className="w-3.5 h-3.5" />SEND MESSAGE</>}
              </button>
            </>
          )}

          {/* Message History */}
          {history.length > 0 && (
            <div className="border-t border-border pt-4">
              <button
                onClick={() => setShowHistory(v => !v)}
                className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors w-full"
              >
                <Clock className="w-3 h-3" />
                Previous Messages ({history.length})
                {showHistory ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
              </button>
              {showHistory && (
                <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                  {history.map(m => (
                    <div key={m.id} className="bg-secondary/30 border border-border rounded-md px-3 py-2.5">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold font-mono text-foreground truncate">{m.subject}</span>
                        <span className="text-[10px] font-mono text-muted-foreground/60 shrink-0 ml-2">
                          {new Date(m.sentAt).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-[10px] font-mono text-muted-foreground leading-relaxed whitespace-pre-wrap line-clamp-3">{m.body}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
